/**
 * Platform Navigation Service
 *
 * Handles API communication for fetching navigation configuration.
 * Navigation is critical infrastructure - errors are thrown (not swallowed)
 * to allow callers to redirect to an error page.
 *
 * Dev toggle: localStorage.setItem('useMockNavigation', 'true') to use mock data
 */

import gatewayService from '@/modules/common/services/gatewayService';
import navigationMock from '../mocks/navigationMock.json';

const MOCK_NAV_KEY = 'useMockNavigation';
const baseRoute = '/v3/apps/navigation_items';

const shouldUseMockData = () => {
  try {
    return localStorage.getItem(MOCK_NAV_KEY) === 'true';
  } catch (e) {
    return false;
  }
};

/**
 * Normalize a single navigation item from dynamicmenus response for frontage.
 *
 * @param {Object} item - Raw item from API
 * @returns {Object} - Item with isBMP and parent_uid set for consistent use in store/tree
 */
const normalizeNavigationItem = (item) => {
  if (!item || typeof item !== 'object') return item;  
  const isBMP = item.is_bmp === true;
  return {
    ...item,
    isBMP,
  };
};

/**
 * Fetch navigation items from the API
 *
 * @returns {Promise<Array>} - Array of navigation items
 * @throws {Error} - Throws on API failure (navigation is critical)
 */
const fetchNavigation = async () => {
  if (shouldUseMockData()) {
    console.info('[POV] platform nav: using mock data (localStorage flag)');
    return [...navigationMock];
  }

  try {
    const response = await gatewayService.get(baseRoute);
    const rawItems = response.data?.data?.navigation_items;
    const items = Array.isArray(rawItems) ? rawItems.map(normalizeNavigationItem) : [];
    if (process.env.NODE_ENV !== 'production') {
      console.info('[POV] platform nav: GET', baseRoute, 'OK, navigation_items count:', items.length);
    }
    return items;
  } catch (error) {
    const status = error?.response?.status;
    const data = error?.response?.data;
    const requestURL = error?.config?.baseURL != null ? `${error.config.baseURL.replace(/\/$/, '')}${baseRoute}` : baseRoute;
    const errors = data?.errors;
    const backendMessage = Array.isArray(errors) && errors.length > 0 ? errors[0] : data?.message ?? data?.error ?? JSON.stringify(data);
    console.error(
      '[POV] platform nav: fetch failed – menu will not show. URL:',
      requestURL,
      'Status:',
      status ?? 'N/A (e.g. network/CORS)',
      'Message:',
      error?.message || error,
      'Backend:',
      backendMessage
    );
    throw error;
  }
};

export default {
  fetchNavigation,
};
